// src/contexts/Cart.context.jsx

'use client';

import { createContext, useCallback, useEffect, useState, useMemo, useContext } from 'react';
import { Money } from '@/lib/money.utils';
import { currencies } from '@/lib/currencies.utils';


const addCartItem = (cartItems, productToAdd) => {
  const existingCartItem = cartItems.find(
    (cartItem) => cartItem.productID === productToAdd.productID
  );

  if (existingCartItem && productToAdd.manufacturing === 0) {
    // If found and hand-made, don't add more
    return cartItems;
  } else if (existingCartItem) {
    // If existed, increment quantity
    return cartItems.map((cartItem) =>
      cartItem.productID === productToAdd.productID
        ? { ...cartItem, quantity: cartItem.quantity + 1 }
        : cartItem
    );
  } else {
    // Item not there before
    return [
      ...cartItems,
      {
        ...productToAdd,
        quantity: 1,
        titleAtCheckout: productToAdd.titleAtCheckout,
        title: productToAdd.title,
      },
    ];
  }
};

const removeCartItem = (cartItems, cartItemToRemove) => {
  const existingCartItem = cartItems.find(
    (cartItem) => cartItem.productID === cartItemToRemove.productID
  );

  if (existingCartItem.quantity === 1) {
    return cartItems.filter(
      (cartItem) => cartItem.productID !== cartItemToRemove.productID
    );
  }

  return cartItems.map((cartItem) =>
    cartItem.productID === cartItemToRemove.productID
      ? { ...cartItem, quantity: cartItem.quantity - 1 }
      : cartItem
  );
};

const clearCartItem = (cartItems, cartItemToClear) =>
  cartItems.filter((cartItem) => cartItem.productID !== cartItemToClear.productID);

const CartContext = createContext({
  cartItems: [],
  addItemToCart: () => {},
  removeItemFromCart: () => {},
  clearItemFromCart: () => {},
  flushCart: () => {},
  cartCount: 0,
  cartTotal: {}, // Object with currency codes as keys and Money objects as values
  getCartTotalInCurrency: () => 0, // Helper to get total in a specific currency
  formatCartTotalInCurrency: () => '', // Helper to format total in a specific currency
});

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    // Read cart from storage if it exists
    try {
      const storedCart = localStorage.getItem('cart');
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        if (Array.isArray(parsedCart)) {
          setCartItems(parsedCart);
        }
      }
    } catch (error) {
      console.error('Error reading cart from localStorage:', error);
    }
  }, []);

  useEffect(() => {
    // Store cart in localStorage
    try {
      const stringCart = JSON.stringify(cartItems);
      localStorage.setItem('cart', stringCart);
    } catch (error) {
      console.error('Error writing cart to localStorage:', error);
    }
  }, [cartItems]);

  useEffect(() => {
    // Sync cart across tabs
    const handleStorageChange = (event) => {
      if (event.key === 'cart') {
        try {
          const newValue = event.newValue;
          if (newValue) {
            const parsedCart = JSON.parse(newValue);
            if (Array.isArray(parsedCart)) {
              setCartItems(parsedCart);
            }
          } else {
            // If newValue is null, the cart was cleared
            setCartItems([]);
          }
        } catch (error) {
          console.error('Error parsing cart from storage event:', error);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const cartCount = useMemo(
    () => cartItems.reduce((total, cartItem) => total + cartItem.quantity, 0),
    [cartItems]
  );

  // New implementation for cartTotal using Money objects
  const cartTotal = useMemo(() => {
    // Define supported currencies
    const supportedCurrencies = Object.keys(currencies).filter(code => currencies[code].active);

    // Initialize totals object with zeros for each currency
    const totals = {};
    supportedCurrencies.forEach(currency => {
      totals[currency] = new Money('0', currency);
    });

    // Calculate totals for each currency using Money objects
    cartItems.forEach(cartItem => {
      supportedCurrencies.forEach(currencyCode => {
        // Skip currencies not available in this product's pricing
        if (!cartItem.pricing?.current?.[currencyCode] && !cartItem.pricing?.original?.[currencyCode]) {
          return;
        }

        // Get the price in this currency (current or original)
        const priceStr = cartItem.pricing?.current?.[currencyCode] ||
                          cartItem.pricing?.original?.[currencyCode] ||
                          (currencyCode === 'EUR' ? cartItem.price?.toString() : '0');

        const itemPrice = new Money(priceStr, currencyCode);
        const itemTotal = itemPrice.multiply(cartItem.quantity);

        // Add to running total
        totals[currencyCode] = totals[currencyCode].add(itemTotal);
      });
    });

    // Round all totals to appropriate decimal places
    supportedCurrencies.forEach(currencyCode => {
      totals[currencyCode] = totals[currencyCode].round();
    });

    return totals;
  }, [cartItems]);

  // Helper function to get total in a specific currency as a Money object
  const getCartTotalInCurrency = useCallback((currencyCode) => {
    return cartTotal[currencyCode] || new Money('0', currencyCode);
  }, [cartTotal]);

  // Helper function to format total in a specific currency
  const formatCartTotalInCurrency = useCallback((currencyCode, locale = 'en-US') => {
    const total = getCartTotalInCurrency(currencyCode);
    return total.format(locale);
  }, [getCartTotalInCurrency]);

  const addItemToCart = useCallback((productToAdd) => {
    setCartItems(prevItems => addCartItem(prevItems, productToAdd));
  }, []);

  const removeItemFromCart = useCallback((cartItemToRemove) => {
    setCartItems(prevItems => removeCartItem(prevItems, cartItemToRemove));
  }, []);

  const clearItemFromCart = useCallback((cartItemToClear) => {
    setCartItems(prevItems => clearCartItem(prevItems, cartItemToClear));
  }, []);

  const flushCart = useCallback(() => {
    setCartItems([]);
    try {
      localStorage.removeItem('cart');
    } catch (error) {
      console.error('Error removing cart from localStorage:', error);
    }
  }, []);

  const value = {
    cartItems,
    addItemToCart,
    removeItemFromCart,
    clearItemFromCart,
    flushCart,
    cartCount,
    cartTotal,
    getCartTotalInCurrency,
    formatCartTotalInCurrency,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};


export const useCart = () => useContext(CartContext);
